/**
 * Column definitions for the members list table.
 *
 * The M1 list view shows `ext_account_id`, `legacy_id`, `provider`,
 * `account_currency`, `status`, `applied_at`, `id`. PII-bearing columns
 * (`legacy_username`, `bank_account_number`, `sort_code`, `iban`) are
 * intentionally absent; they live behind `MaskedField` on the detail page.
 *
 * ## Labels are staff vocabulary, not schema vocabulary
 *
 * Headers previously rendered the raw column names (`ext_account_id`,
 * `applied_at`) while `aria-label` carried the human name - so a screen
 * reader announced "External account id" and a support agent on a call
 * read `ext_account_id`. That is backwards. The visible label is now the
 * human one; `aria-label` remains for the cases where the visible label is
 * necessarily terse, and stays consistent with it.
 *
 * The underlying field names are unchanged - they are the shape
 * `sm2-account-service` publishes, and the generated client is checked for
 * drift in CI. This is a presentation layer only.
 *
 * ## Order
 *
 * `ext_account_id` leads because it is the value a caller reads out and
 * the one staff quote back. The SM2 `id` is a UUIDv7 whose rendered form
 * is a truncated tail (`...000000000001`); it is near-useless for scanning
 * and sits last, where it is available for support escalation without
 * occupying the eye's first stop.
 */

import type { Account } from '../../lib/bff-client'
import { shortenId } from '../../lib/id'
import { StatusPill } from '../../components/status-pill'

export interface ColumnDef {
  /** Stable key for React reconciliation. */
  readonly key: keyof Account | 'id_short'
  /** Visible column header text. */
  readonly label: string
  /** Accessible name (read aloud by screen readers). */
  readonly ariaLabel: string
  /** Optional Tailwind classes applied to the cell. */
  readonly className?: string
  /** Render fn for the cell value. */
  render(account: Account): React.ReactNode
}

export const ACCOUNT_COLUMNS: readonly ColumnDef[] = [
  {
    key: 'ext_account_id',
    label: 'Account number',
    ariaLabel: 'Account number',
    className: 'font-mono text-murmuration-code font-medium text-text-primary',
    render: (a) => a.ext_account_id,
  },
  {
    key: 'legacy_id',
    label: 'Legacy id',
    ariaLabel: 'Legacy id',
    className: 'font-mono text-murmuration-code text-text-secondary',
    render: (a) => String(a.legacy_id),
  },
  {
    key: 'provider',
    label: 'Provider',
    ariaLabel: 'Banking provider',
    className: 'text-murmuration-body text-text-secondary',
    render: (a) => a.provider,
  },
  {
    key: 'account_currency',
    label: 'Currency',
    ariaLabel: 'Account currency',
    className: 'text-murmuration-body text-text-secondary',
    render: (a) => a.account_currency,
  },
  {
    key: 'status',
    label: 'Status',
    ariaLabel: 'Account status',
    render: (a) => <StatusPill status={a.status} />,
  },
  {
    key: 'applied_at',
    label: 'Last updated',
    ariaLabel: 'Last updated - when the SM2 projection last applied this row',
    className: 'whitespace-nowrap text-murmuration-body text-text-secondary',
    render: (a) => formatTimestamp(a.applied_at),
  },
  {
    key: 'id',
    label: 'SM2 id',
    ariaLabel: 'SM2 id - the internal record identifier',
    className: 'font-mono text-murmuration-code text-text-muted',
    render: (a) => <span title={a.id}>{shortenId(a.id)}</span>,
  },
]

function formatTimestamp(iso: string): string {
  // ISO-8601 is the source format. Render as `YYYY-MM-DD HH:mm` UTC,
  // matching the dense, tool-like aesthetic in 04c and avoiding the
  // locale ambiguity that would come from `toLocaleString()`.
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(
      d.getUTCHours(),
    )}:${pad(d.getUTCMinutes())}`
  } catch {
    return iso
  }
}
